import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { X, Trash2, Pencil, ArrowUp, ArrowDown, Star } from "lucide-react";
import { fmtCurrency, fmtPct, fmtDateTime } from "../lib/formatters";
import ElementChangesModal from "./ElementChangesModal";
import ConfirmActionModal from "./ConfirmActionModal";
import { useAuth, isAdmin, isViewOnly } from "../contexts/AuthContext";
import { useAutoDismiss } from "../hooks/useAutoDismiss";
import { ScenarioCreatorFilter, listCreators, creatorKey } from "./ScenarioCreatorFilter";
import { GoToNameDialog } from "./GoToNameDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Truncated Scenario ID with a full-text hover label (portaled so the table's scroll box can't clip it).
function ScenarioIdLabel({ scenarioId, identifier }) {
  const [pos, setPos] = useState(null);
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4 });
  };
  return (
    <>
      <span
        className="block truncate text-ink tabular-nums cursor-default"
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
        data-testid={`scenario-id-${scenarioId}`}
      >
        {identifier || "—"}
      </span>
      {pos && identifier && createPortal(
        <span
          className="pointer-events-none fixed whitespace-nowrap rounded-[3px] border border-[#EA6A47] bg-[#1C4E80] text-white text-[11px] font-semibold px-2 py-0.5"
          style={{ left: pos.left, top: pos.top, zIndex: 1000 }}
          data-testid={`scenario-id-hover-${scenarioId}`}
        >
          {identifier}
        </span>,
        document.body,
      )}
    </>
  );
}


// 2026-03-01 (Dave) — Clickable column header. Renders label + an up/down
// caret when this column is the active sort. Clicking a different column
// switches to it (asc for strings, desc for numbers/dates); clicking the
// same column flips direction. Kept dumb — parent owns `sort` state.
// Centers a right-aligned fixed-width block so last digits line up vertically.
function CenteredNum({ ch, children }) {
  return <span style={{ display: "inline-block", minWidth: `${ch}ch`, textAlign: "right" }}>{children}</span>;
}
const maxLen = (rows, fn) => rows.reduce((m, r) => Math.max(m, String(fn(r) ?? "").length), 1);
const widthCache = new WeakMap();
const colWidths = (rows) => {
  if (!widthCache.has(rows)) {
    widthCache.set(rows, {
      adj: maxLen(rows, (r) => r.adjustment_period),
      y1: maxLen(rows, (r) => fmtCurrency(r.year1_budget)),
      sa: maxLen(rows, (r) => fmtCurrency(r.special_assessments)),
      fy: maxLen(rows, (r) => fmtCurrency(r.final_year_budget)),
    });
  }
  return widthCache.get(rows);
};

function SortableTh({ label, sortKey, align = "left", minWidth, sort, onSort, title, arrow }) {
  const active = sort.key === sortKey;
  const Caret = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : arrow ? ArrowDown : null;
  const caretSize = arrow === "coral" ? 15 : arrow ? 13 : 10;
  const caretColor = arrow === "coral" ? "text-coral" : "text-navy";
  const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <th
      style={{
        position: "sticky",
        top: 0,
        background: "var(--u-navy-wash)",
        ...(minWidth ? { minWidth } : {}),
      }}
      className={align === "right" ? "text-right" : align === "center" ? "text-center" : undefined}
      data-testid={`scenario-history-th-${sortKey}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={title || `Sort by ${label}`}
        className={`w-full flex items-center gap-1 ${justify} text-inherit hover:text-navy focus:outline-none`}
      >
        <span>{label}</span>
        {Caret && <Caret size={caretSize} strokeWidth={arrow ? 2.5 : 2} className={`${caretColor} shrink-0`} data-testid={`scenario-history-sort-arrow-${sortKey}`} />}
      </button>
    </th>
  );
}

// Editable-on-click cell used by both the name and user-name columns.
// 2026-02-27 (evening, Dave) — Now STAGES its edits instead of
// PATCHing them inline. The parent modal collects staged values in
// its `pendingEdits` map and flushes them via a Save button in the
// footer, matching the app-wide Elements/Variables editing pattern.
// `pristineValue` = server value, `stagedValue` = current pending
// value (undefined if none staged). Display prefers staged. On
// commit, the cell calls `onStage(field, next)`; passing `undefined`
// as `next` (i.e., reverting back to pristine) clears the staged
// entry.
function EditableScenarioField({
  scenario,
  field,          // "name" | "user_name"
  pristineValue,  // server value
  stagedValue,    // parent-staged value (undefined if not staged)
  placeholder,    // shown when value is empty (e.g. "—")
  extraBelow,     // JSX rendered under the value in view mode (e.g. baseline+date)
  onStage,        // (field, nextValue|undefined) => void
  testidPrefix,   // e.g. "scenario-name" or "scenario-user"
  readOnly,       // Slice 5i — when true, render plain text (no click-to-edit)
  readOnlyReason, // tooltip explaining why it's locked
  maxLength = 56,
}) {
  const displayValue = stagedValue !== undefined ? stagedValue : pristineValue;
  const isDirty = stagedValue !== undefined && stagedValue !== (pristineValue || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayValue || "");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Slice E5l (2026-02-09) — position caret AFTER the last character
      // rather than selecting everything, so a click-to-edit doesn't wipe
      // the value on the next keystroke.
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  useEffect(() => { setDraft(displayValue || ""); }, [displayValue]);

  const commit = () => {
    const next = (draft || "").trim();
    setErr("");
    // Revert to pristine → clear the stage. Otherwise stage next.
    if (next === (pristineValue || "")) {
      onStage(field, undefined);
    } else {
      onStage(field, next);
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(displayValue || "");
    setEditing(false);
    setErr("");
  };

  const sid = scenario.scenario_id;

  if (editing) {
    return (
      <div>
        <input
          ref={inputRef}
          type="text"
          maxLength={maxLength}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          onBlur={commit}
          className="w-full border border-navy px-1 py-0.5 text-[12px] text-ink bg-white focus:outline-none"
          data-testid={`${testidPrefix}-input-${sid}`}
        />
        {err && (
          <div
            className="text-[10px] text-coral mt-0.5"
            data-testid={`${testidPrefix}-error-${sid}`}
          >
            {err}
          </div>
        )}
      </div>
    );
  }

  const isEmpty = !displayValue;

  // Slice 5i (2026-02-25) — role/ownership gate. Renders the label as
  // plain text (no click-to-edit affordance, no pencil icon).
  if (readOnly) {
    return (
      <div className="min-w-0">
        <span
          className={`block truncate ${isEmpty ? "text-[#A3ADB8]" : "text-ink"}`}
          title={readOnlyReason || (isEmpty ? undefined : displayValue)}
          data-testid={`${testidPrefix}-readonly-${sid}`}
        >
          {isEmpty ? (placeholder || "—") : displayValue}
        </span>
        {extraBelow}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {/* Slice E5m (2026-02-09) — name is a plain span; only the pencil
          button invokes edit mode. Previously the whole row was a button
          so a stray click anywhere on the label would drop the user into
          the input, which Dave found jarring. */}
      <div className="group flex items-center gap-1 w-full min-w-0">
        <span
          className={`truncate min-w-0 flex-1 ${isEmpty ? "text-[#A3ADB8]" : isDirty ? "text-navy font-semibold" : "text-ink"}`}
          title={isEmpty ? undefined : displayValue}
          data-testid={`${testidPrefix}-value-${sid}`}
        >
          {isEmpty ? (placeholder || "—") : displayValue}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Edit"
          aria-label="Edit"
          className="shrink-0 text-slate hover:text-navy focus:text-navy focus:outline-none rounded-[3px] p-0.5"
          data-testid={`${testidPrefix}-edit-${sid}`}
        >
          <Pencil size={10} />
        </button>
      </div>
      {extraBelow}
    </div>
  );
}

// 2026-02-27 (evening, Dave) — Editable numeric cell used by the
// variable columns (target %, inflation %, interest %, adjustment %,
// adjustment period). Click the value → inline `<input type="number">`
// → Enter/blur STAGES via `onStage(field, next)` on the parent modal
// (the parent flushes all staged edits when the user hits Save);
// Escape cancels. `kind` picks the format/parse pair so the same
// component handles a percent (stored 0..1, displayed as e.g. "40%")
// and an integer year count.
function EditableScenarioNumber({
  scenario,
  field,          // e.g. "target_pct", "adjustment_period"
  pristineValue,  // server value (0..1 for pct, int for years)
  stagedValue,    // parent-staged value (undefined if not staged)
  kind,           // "pct" | "pctFractional" | "int"
  min,
  max,
  step,
  onStage,        // (field, nextValue|undefined) => void
  readOnly,
  readOnlyReason,
  testidPrefix,
}) {
  const currentValue = stagedValue !== undefined ? stagedValue : pristineValue;
  const isDirty = stagedValue !== undefined && Number(stagedValue) !== Number(pristineValue || 0);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  // Format for view mode / seeding the input.
  const toDisplay = (v) => {
    if (v === null || v === undefined || v === "") return "";
    if (kind === "pct") return String(Math.round(Number(v) * 100));
    if (kind === "pctFractional") return (Number(v) * 100).toFixed(2);
    return String(Math.round(Number(v)));
  };
  const fromDisplay = (s) => {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return null;
    if (kind === "pct" || kind === "pctFractional") return n / 100;
    return Math.round(n);
  };

  const [draft, setDraft] = useState(toDisplay(currentValue));
  const [err, setErr] = useState("");

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);
  useEffect(() => { setDraft(toDisplay(currentValue)); }, [currentValue, kind]);

  const commit = () => {
    const raw = (draft || "").trim();
    if (!raw) {
      setErr("Value required");
      return;
    }
    const parsed = fromDisplay(raw);
    if (parsed === null) {
      setErr("Not a number");
      return;
    }
    setErr("");
    const before = Number(pristineValue) || 0;
    // Revert to pristine → clear the stage.
    if (kind === "int" ? parsed === before : Math.abs(parsed - before) < 1e-9) {
      onStage(field, undefined);
    } else {
      onStage(field, parsed);
    }
    setEditing(false);
  };
  const cancel = () => {
    setDraft(toDisplay(currentValue));
    setEditing(false);
    setErr("");
  };

  const sid = scenario.scenario_id;
  const displayText = kind === "int"
    ? String(Math.round(Number(currentValue) || 0))
    : kind === "pctFractional"
      ? `${((Number(currentValue) || 0) * 100).toFixed(2)}%`
      : `${Math.round((Number(currentValue) || 0) * 100)}%`;

  if (readOnly) {
    return (
      <span
        className="text-ink"
        title={readOnlyReason}
        data-testid={`${testidPrefix}-readonly-${sid}`}
      >
        {displayText}
      </span>
    );
  }

  if (editing) {
    return (
      <div className="inline-flex flex-col items-end">
        <input
          ref={inputRef}
          type="number"
          min={min}
          max={max}
          step={step}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          onBlur={commit}
          className="w-16 border border-navy px-1 py-0.5 text-[12px] font-mono tabular-nums text-right text-ink bg-white focus:outline-none"
          data-testid={`${testidPrefix}-input-${sid}`}
        />
        {err && (
          <div
            className="text-[10px] text-coral mt-0.5"
            data-testid={`${testidPrefix}-error-${sid}`}
          >
            {err}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`focus:outline-none underline-offset-2 hover:underline ${isDirty ? "text-navy font-semibold" : "text-ink hover:text-navy focus:text-navy"}`}
      data-testid={`${testidPrefix}-value-${sid}`}
      title="Click to edit"
    >
      {displayText}
    </button>
  );
}

const VALUES_LOCKED = "Saved values are locked. Change them on the Dashboard and Save a new scenario.";

// Scenario History popup (Screen 8) — 12 columns matching Pixel Dave's spec:
// Go To · Scenario · Scenario Name · User Name · Target % · Inflation % · Interest %
// · Adjustment % · Year-1 % (period) · Year-1 Budget · Special Assessments · Delete
export default function ScenarioHistoryModal({
  associationName,
  scenarios,
  activeScenarioId,
  onGoTo,
  onDeleted,
  onRenamed,
  onClose,
}) {
  // Exempt from timed dismiss: stays open while idle, and holds the app's screen snapback.
  useEffect(() => {
    document.body.dataset.idleHold = "1";
    return () => { delete document.body.dataset.idleHold; };
  }, []);
  const { user } = useAuth();
  const viewer = user || {};
  const viewerIsAdmin = isAdmin(viewer);
  const viewerIsViewOnly = isViewOnly(viewer);
  const viewerId = viewer?.user_id ? String(viewer.user_id) : "";


  // 2026-03-01 (Dave) — Sort: default newest-first by created_at DESC.
  // Click any column header to resort by that key; click the SAME header
  // again to flip direction. Rules:
  //   * The `#` column is gone (no per-row ordinal displayed).
  //   * A scenario cannot be deleted if it's the only one — need >=2
  //     to remove 1. Applies to baseline AND non-baseline rows alike.
  // 2026-02-28 (Dave) — Default sort switched from persisted
  // sort_order (drag-and-drop) → created_at DESC. Newest scenarios
  // rise to the top and the coral-star Go-To no longer pins.
  // 2026-06 (owner) — default sort: Target % highest → lowest (ties: oldest first).
  const DEFAULT_SORT = { key: "target_pct", dir: "desc" };
  const [sort, setSort] = useState(DEFAULT_SORT);
  const STRING_KEYS = React.useMemo(() => new Set(["identifier", "name", "user_name", "notes"]), []);
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) {
        return { key, dir: STRING_KEYS.has(key) ? "asc" : "desc" };
      }
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  // 2026-03-09 (Dave) — Local mirror of scenarios (declared HERE so
  // `sortedScenarios` below can reference it). Kept in sync with the
  // parent's `scenarios` snapshot via useEffect. All Go-To flips and
  // drag reorders update this mirror optimistically before firing
  // the corresponding PATCH — see flipGoTo / persistOrder farther
  // down for the write path.
  const [localScenarios, setLocalScenarios] = useState([]);
  useEffect(() => {
    setLocalScenarios(Array.isArray(scenarios) ? scenarios : []);
  }, [scenarios]);

  const tableRef = useRef(null);
  const [sortColIdx, setSortColIdx] = useState(null);
  useEffect(() => {
    const th = tableRef.current?.querySelector(`[data-testid="scenario-history-th-${sort.key}"]`);
    setSortColIdx(th ? th.cellIndex + 1 : null);
  }, [sort.key]);

  const firstYear = scenarios[0]?.first_year;
  const finalYear = scenarios[0]?.final_year;

  const sortedScenarios = useMemo(() => {
    // 2026-02-28 (Dave) — Sort order simplified.  The coral-star
    // Go-To no longer pins to the top — it lives in the list at its
    // natural position (created_at desc, newest first).  Column
    // header clicks still override (e.g. sort by Target %); the
    // final tiebreaker is created_at DESC so identical values still
    // put the newest row first.
    const arr = (Array.isArray(localScenarios) ? localScenarios : []).slice();
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av, bv;
      if (key === "created_at") {
        av = a.created_at ? new Date(a.created_at).getTime() : 0;
        bv = b.created_at ? new Date(b.created_at).getTime() : 0;
      } else if (key === "sort_order") {
        av = Number(a.sort_order || 0);
        bv = Number(b.sort_order || 0);
      } else if (STRING_KEYS.has(key)) {
        av = (a[key] || "").toString().toLowerCase();
        bv = (b[key] || "").toString().toLowerCase();
      } else {
        av = Number(a[key]) || 0;
        bv = Number(b[key]) || 0;
      }
      if (av < bv) return -1 * mul;
      if (av > bv) return 1 * mul;
      // Tiebreaker — Target % ties: oldest first; all other columns: newest first.
      const at = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
      return key === "target_pct" ? at - bt : bt - at;
    });
    return arr;
  }, [localScenarios, sort, STRING_KEYS]);

  // 2026-11 (owner) — creator filter; opens on the viewer's own scenarios.
  const [creatorSel, setCreatorSel] = useState(() => new Set([viewerId]));
  const creators = useMemo(() => listCreators(localScenarios, viewerId), [localScenarios, viewerId]);
  const visibleScenarios = useMemo(
    () => sortedScenarios.filter((s) => creatorSel.has(creatorKey(s))),
    [sortedScenarios, creatorSel],
  );

  // Slice 5i (2026-02-25) — canonical role/ownership rules for a single row:
  //   * View-only users can never mutate (they can Go-To only).
  //   * Baseline scenarios: 2026-03-01 (Dave) — admins may delete IF another
  //     scenario exists to auto-promote as replacement. Analysts cannot
  //     delete baseline (matches the "baseline is a big deal" guardrail).
  //   * Non-baseline: Admins can delete any; analysts only rows they
  //     authored themselves (`created_by_user_id === user.user_id`). Legacy
  //     scenarios that predate author tracking (created_by_user_id == null)
  //     are treated as "not mine" for analysts.
  //   * Universal (2026-03-01): if only 1 scenario exists in the plan year,
  //     it can't be deleted (need >=2).
  const totalScenarios = Array.isArray(scenarios) ? scenarios.length : 0;
  const hasReplacementForBaseline = useMemo(
    () => (Array.isArray(scenarios) ? scenarios.some((x) => !x.is_baseline) : false),
    [scenarios]
  );
  const canMutateScenario = (s) => {
    if (!s) return false;
    if (!viewer || viewerIsViewOnly) return false;
    if (totalScenarios < 2) return false;
    if (s.is_baseline) {
      // Only admins, and only when a replacement exists.
      return viewerIsAdmin && hasReplacementForBaseline;
    }
    if (viewerIsAdmin) return true;
    const owner = s.created_by_user_id ? String(s.created_by_user_id) : "";
    return !!owner && !!viewerId && owner === viewerId;
  };
  // Slice 5j (2026-02-09) — rename is allowed even on baselines because
  // the label is just a display string; only delete stays locked.
  const canRenameScenario = (s) => {
    if (!s) return false;
    if (!viewer || viewerIsViewOnly) return false;
    if (viewerIsAdmin) return true;
    const owner = s.created_by_user_id ? String(s.created_by_user_id) : "";
    return !!owner && !!viewerId && owner === viewerId;
  };
  const lockReason = (s) => {
    if (!s) return "";
    if (viewerIsViewOnly) return "View-only users cannot modify scenarios.";
    if (totalScenarios < 2)
      return "Save another scenario first — at least two are required to delete one.";
    if (s.is_baseline) {
      if (!viewerIsAdmin) return "Only administrators can delete the baseline scenario.";
      if (!hasReplacementForBaseline)
        return "Save another scenario first — one is required to promote as the new baseline.";
      return "";
    }
    if (!viewerIsAdmin) return "Only the author or an administrator can modify this scenario.";
    return "";
  };
  const renameLockReason = (s) => {
    if (!s) return "";
    if (viewerIsViewOnly) return "View-only users cannot rename scenarios.";
    if (viewerIsAdmin) return "";
    return "Only the author or an administrator can rename this scenario.";
  };

  const [busy, setBusy] = useState(null); // scenario_id currently being deleted
  const [error, setError] = useState(null);
  const [deltaScenario, setDeltaScenario] = useState(null); // scenario whose element changes we're viewing
  // Confirm-delete branded modal (replaces window.confirm). Holds the row
  // pending confirmation; null = no dialog visible.
  const [pendingDelete, setPendingDelete] = useState(null);

  // 2026-02-27 (evening, Dave) — Staged edits (`pendingEdits`) live at
  // the modal level. Shape: `{ [scenario_id]: { [field]: value } }`.
  // Cells call `stageEdit(sid, field, value)` on commit; the Save
  // button in the footer flushes every staged scenario in one
  // PATCH per row, then updates the parent scenarios array via
  // `onRenamed(...)`. Passing `undefined` as the value clears that
  // field's stage (used when a user reverts a cell to its pristine
  // value).
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingEdits, setSavingEdits] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // 2026-03-09 (Dave) — Go-To flip + drag-and-drop write path.
  // `localScenarios` is declared above so `sortedScenarios` can see
  // it. Below we set up the ancillary state (dragging + goToBusy)
  // and the mutation callbacks.
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [goToBusy, setGoToBusy] = useState(null);
  const [goToError, setGoToError] = useState(null);
  useAutoDismiss(goToError, () => setGoToError(null));

  const [namePrompt, setNamePrompt] = useState(null); // { row, busy, error }
  const submitGoToName = async (name) => {
    const row = namePrompt.row;
    setNamePrompt((p) => ({ ...p, busy: true, error: null }));
    try {
      await axios.patch(`${API}/scenarios/${row.scenario_id}`, { name });
    } catch (e) {
      setNamePrompt((p) => ({ ...p, busy: false, error: e?.response?.data?.detail || e.message || "Could not save the name." }));
      return;
    }
    setNamePrompt(null);
    setLocalScenarios((prev) => prev.map((s) => (s.scenario_id === row.scenario_id ? { ...s, name } : s)));
    flipGoTo({ ...row, name });
  };

  const flipGoTo = async (row) => {
    if (viewerIsViewOnly) return;
    if (goToBusy) return;
    // The star always sits on exactly one scenario: move it by starring another one.
    if (row.is_go_to) {
      setGoToError("This is the Go-To scenario. Star a different scenario to change it.");
      return;
    }
    if (!String(row.name || "").trim()) {
      setNamePrompt({ row, busy: false, error: null });
      return;
    }
    const newVal = true;
    setGoToBusy(row.scenario_id);
    setGoToError(null);
    // Optimistic swap: any row that was is_go_to becomes false; this
    // row becomes newVal. Mirrors backend single-writer semantics.
    setLocalScenarios((prev) => prev.map((s) => {
      if (s.scenario_id === row.scenario_id) return { ...s, is_go_to: newVal, include_in_report: false };
      if (newVal && s.is_go_to) return { ...s, is_go_to: false };
      return s;
    }));
    try {
      await axios.patch(`${API}/scenarios/${row.scenario_id}/go-to`, { is_go_to: newVal });
      // 2026-02-28 (Dave, bug) — flipping the coral star is the way
      // to *choose* the default scenario, so the Dashboard + all
      // downstream calc consumers must reload with the new default's
      // variables. Previously the PATCH updated only the flag, which
      // meant the star moved but the Dashboard kept showing the
      // previously-active scenario's numbers.  Trigger the same load
      // path used by the "Go To" button (`onGoTo` here) so sliders,
      // dashboards, contributions and reports all snap to the newly
      // starred scenario.  Only fire on newVal=true (turning a star
      // OFF is only allowed when a different row is being turned ON
      // at the same time, which is handled server-side).
      if (newVal && typeof onGoTo === "function") {
        onGoTo(row, { toDashboard: true }); // star click loads it, closes the modal, lands on Dashboard
      }
    } catch (e) {
      setGoToError(e?.response?.data?.detail || e.message || "Could not update Go To.");
      // Revert
      setLocalScenarios(Array.isArray(scenarios) ? scenarios : []);
    } finally {
      setGoToBusy(null);
    }
  };

  const [includeBusy, setIncludeBusy] = useState(null);
  const MAX_INCLUDED = 3;
  const includedCount = localScenarios.filter((s) => s.is_go_to || s.include_in_report).length;
  const flipInclude = async (row) => {
    if (viewerIsViewOnly || row.is_go_to || includeBusy) return;
    const newVal = !row.include_in_report;
    if (newVal && includedCount >= MAX_INCLUDED) {
      setGoToError(`Up to ${MAX_INCLUDED} scenarios (including the Go-To) can be included in the report. Uncheck one first.`);
      return;
    }
    const setVal = (v) => setLocalScenarios((prev) => prev.map((s) =>
      s.scenario_id === row.scenario_id ? { ...s, include_in_report: v } : s));
    setIncludeBusy(row.scenario_id);
    setVal(newVal);
    try {
      await axios.patch(`${API}/scenarios/${row.scenario_id}/include`, { include_in_report: newVal });
    } catch (e) {
      setVal(!newVal);
      setGoToError(e?.response?.data?.detail || e.message || "Could not update Include.");
    } finally {
      setIncludeBusy(null);
    }
  };

  const persistOrder = async (orderedIds) => {
    // Fire-and-forget PATCH per row (small N — always fewer than a
    // dozen scenarios per plan year). The Go To row is excluded
    // from the persisted order because it always visually pins to
    // the top regardless of sort_order.
    const nonGoIds = orderedIds.filter((id) => {
      const s = localScenarios.find((x) => x.scenario_id === id);
      return s && !s.is_go_to;
    });
    try {
      await Promise.all(nonGoIds.map((id, idx) =>
        axios.patch(`${API}/scenarios/${id}/sort-order`, { sort_order: idx + 1 })
      ));
    } catch (e) {
      setGoToError(e?.response?.data?.detail || e.message || "Could not save the new order.");
    }
  };

  const handleDragStart = (sid) => (e) => {
    setDragging(sid);
    try { e.dataTransfer.effectAllowed = "move"; } catch (_) {}
  };
  const handleDragOver = (sid) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch (_) {}
    if (dragOver !== sid) setDragOver(sid);
  };
  const handleDrop = (targetSid) => (e) => {
    e.preventDefault();
    setDragOver(null);
    const from = dragging;
    setDragging(null);
    if (!from || from === targetSid) return;
    const fromRow = localScenarios.find((s) => s.scenario_id === from);
    if (!fromRow || fromRow.is_go_to) return;    // Go-To row is pinned
    // Rebuild local order: remove `from`, insert before `targetSid`.
    const withoutFrom = localScenarios.filter((s) => s.scenario_id !== from);
    const targetIdx = withoutFrom.findIndex((s) => s.scenario_id === targetSid);
    const inserted = [
      ...withoutFrom.slice(0, targetIdx < 0 ? withoutFrom.length : targetIdx),
      fromRow,
      ...(targetIdx < 0 ? [] : withoutFrom.slice(targetIdx)),
    ];
    // Re-assign sort_order (1..N; 0 reserved for Go To).
    const goToIds = new Set(inserted.filter((s) => s.is_go_to).map((s) => s.scenario_id));
    let idx = 1;
    const reordered = inserted.map((s) => (goToIds.has(s.scenario_id)
      ? { ...s, sort_order: 0 }
      : { ...s, sort_order: idx++ }));
    setLocalScenarios(reordered);
    persistOrder(reordered.map((s) => s.scenario_id));
  };
  const stageEdit = (sid, field, value) => {
    setPendingEdits((prev) => {
      const next = { ...prev };
      const row = { ...(next[sid] || {}) };
      if (value === undefined) {
        delete row[field];
      } else {
        row[field] = value;
      }
      if (Object.keys(row).length === 0) delete next[sid];
      else next[sid] = row;
      return next;
    });
  };
  const discardEdits = () => {
    setPendingEdits({});
    setSaveError(null);
  };
  const flushEdits = async () => {
    if (savingEdits) return false;
    const rows = Object.entries(pendingEdits);
    if (rows.length === 0) return true;
    setSavingEdits(true);
    setSaveError(null);
    // Persist rows serially so a single row's error surfaces cleanly
    // and never wedges the whole batch. On the first failure we
    // stop and leave the remaining rows still staged so the user
    // can retry after fixing the offending value.
    for (const [sid, patch] of rows) {
      try {
        const res = await axios.patch(`${API}/scenarios/${sid}`, patch);
        onRenamed(sid, res.data);
        setPendingEdits((prev) => {
          const { [sid]: _dropped, ...rest } = prev;
          return rest;
        });
      } catch (e) {
        setSaveError(e?.response?.data?.detail || e.message || "Save failed");
        setSavingEdits(false);
        return false;
      }
    }
    setSavingEdits(false);
    return true;
  };
  // Done = save any staged edits, then close (stays open on failure).
  const saveAndClose = async () => {
    if (await flushEdits()) onClose();
  };
  const pendingCount = Object.keys(pendingEdits).length;

  // Warn the user before closing the modal with unsaved edits.
  const requestClose = () => {
    if (pendingCount > 0) {
      const ok = window.confirm(
        `You have unsaved changes on ${pendingCount} scenario${pendingCount === 1 ? "" : "s"}. Discard and close?`
      );
      if (!ok) return;
      setPendingEdits({});
    }
    onClose();
  };

  const requestDelete = (s) => {
    if (!canMutateScenario(s)) return;
    setPendingDelete(s);
  };

  const doDelete = async () => {
    const s = pendingDelete;
    if (!s) return;
    setBusy(s.scenario_id);
    setError(null);
    try {
      const res = await axios.delete(`${API}/scenarios/${s.scenario_id}`);
      onDeleted(s.scenario_id, res.data);
      setPendingDelete(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  // 2026-03-01 (Dave) — When the user is deleting the baseline, compute
  // which scenario the backend will auto-promote (oldest non-baseline in
  // this plan year) so the confirmation modal can name it explicitly.
  const pendingReplacement = useMemo(() => {
    if (!pendingDelete || !pendingDelete.is_baseline) return null;
    const others = (scenarios || []).filter((x) => !x.is_baseline);
    others.sort((a, b) => {
      const at = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
      return at - bt;
    });
    return others[0] || null;
  }, [pendingDelete, scenarios]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      data-testid="scenario-history-modal"
    >
      <div
        className="bg-white w-[1840px] max-w-[99vw] max-h-[85vh] flex flex-col rounded-[5px] overflow-hidden"
        style={{
          border: "2px solid var(--u-coral)",
          boxShadow: "0 0 0 5px var(--u-navy), 0 25px 50px -12px rgba(0,0,0,0.25)",
        }}
        data-testid="scenario-history-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-rule bg-navy-wash">
          <div style={{ marginLeft: -2 }}>
            <div
              className="font-display text-[20px] font-semibold"
              style={{ color: "#0F2A44" }}
              data-testid="scenario-history-title"
            >
              Scenario History
            </div>
            <div className="text-[14px] font-medium" style={{ color: "#4A5560" }} data-testid="scenario-history-subtitle">
              Check <span className="font-semibold" style={{ color: "#EA6A47" }}>Go To</span>{" "}
              on any Scenario's row to load its variables into uReserve's calculation engine and update all views.
            </div>
          </div>
          <div className="flex items-center gap-6 ml-6">
            <ScenarioCreatorFilter creators={creators} viewerId={viewerId} selected={creatorSel} onChange={setCreatorSel} myCount={localScenarios.filter((s) => creatorKey(s) === viewerId).length} />
            <button
              onClick={requestClose}
              className="p-1 hover:bg-white"
              data-testid="scenario-history-close"
            >
              <X size={18} className="text-navy" />
            </button>
          </div>
        </div>

        {error && (
          <div
            className="bg-coral-wash border-y border-coral px-3 py-1 text-[12px] text-ink"
            data-testid="scenario-history-error"
          >
            {error}
          </div>
        )}

        <div className="overflow-auto flex-1">
          {sortColIdx && (
            <style>{`[data-testid="scenario-history-table"] tr > :nth-child(${sortColIdx}):not([colspan]) { box-shadow: inset 0 0 0 9999px rgba(0,145,213,0.08); }`}</style>
          )}
          <table ref={tableRef} className="u-table u-table-dense w-full" data-testid="scenario-history-table" data-sort-col={sortColIdx || ""}>
            <thead>
              <tr>
                {/* 2026-03-09 (Dave) — Severed-scenarios column set.
                    Anchor row is filtered out entirely; drag handle
                    pins on the left so users can reorder non-Go-To
                    scenarios; coral ★ column holds the persistent
                    Go-To flag (PATCH /scenarios/{id}/go-to). */}
                <th
                  style={{
                    position: "sticky", top: 0,
                    background: "var(--u-navy-wash)", width: 60,
                    textAlign: "center",
                  }}
                  title="Adopted plan — the scenario that Dashboard, Projections, and the Report default to"
                >
                  <span style={{ position: "relative", left: 2 }}>Go To</span>
                </th>
                <th
                  style={{
                    position: "sticky", top: 0,
                    background: "var(--u-navy-wash)", width: 60,
                    textAlign: "center",
                  }}
                  title="Show this scenario in the report's Scenarios Considered table (max 3, including the Go-To)"
                >
                  Include
                </th>
                <SortableTh
                  label="Scenario ID"
                  sortKey="identifier"
                  minWidth={300}
                  sort={sort}
                  onSort={toggleSort}
                  title="Built from the scenario's values when saved; never changes"
                />
                <SortableTh
                  label="Scenario Name"
                  sortKey="name"
                  minWidth={250}
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label="Created By"
                  sortKey="user_name"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label={"Target\u00a0%"}
                  sortKey="target_pct"
                  arrow="coral"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label={"Inflation\u00a0%"}
                  sortKey="inflation_rate"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label={"Interest\u00a0%"}
                  sortKey="interest_rate"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label={"Adjustment\u00a0%"}
                  sortKey="adjustment_pct"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label="Adj. Years"
                  sortKey="adjustment_period"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                  title="Number of years over which the Year-1 adjustment is systematically eliminated"
                />
                <SortableTh
                  label={`${firstYear ?? "Year 1"} Budget`}
                  sortKey="year1_budget"
                  arrow="navy"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label={<>Special<br />Assessments</>}
                  sortKey="special_assessments"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                  title="Sort by Special Assessments"
                />
                <SortableTh
                  label={`${finalYear ?? "Final Year"} Budget`}
                  sortKey="final_year_budget"
                  arrow="navy"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh
                  label={<>Funding&nbsp;%<br />{finalYear ?? "Yr 30"}</>}
                  sortKey="final_funding_pct"
                  arrow="navy"
                  align="center"
                  sort={sort}
                  onSort={toggleSort}
                  title="Sort by Funding % at the 30-year mark"
                />
                {/* 2026-03-09 (Dave) — "# Chg" column removed under
                    the severed-scenarios architecture. Element edits
                    are no longer scenario-scoped; per-element history
                    lives in the Element History drawer on the
                    Elements tab. */}
                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "var(--u-navy-wash)",
                    width: 46,
                    textAlign: "center",
                  }}
                >
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleScenarios.map((s, _i, rows) => {
                const w = colWidths(rows);
                const checked = s.scenario_id === activeScenarioId;
                const mutable = canMutateScenario(s);
                const lockedReason = lockReason(s);
                const renamable = canRenameScenario(s);
                const renameLocked = renameLockReason(s);
                const d = s;
                return (
                  <tr
                    key={s.scenario_id}
                    data-testid={`scenario-row-${s.scenario_id}`}
                    style={{
                      background: checked
                        ? "var(--u-sky-wash)"
                        : (dragOver === s.scenario_id && !s.is_go_to ? "#FEF5F2"
                          : (creatorKey(s) === viewerId ? "#EDF6FC" : undefined)),
                      fontWeight: checked ? 600 : 400,
                      opacity: dragging === s.scenario_id ? 0.4 : 1,
                      outline: s.is_go_to ? "2px solid rgba(234,106,71,0.55)" : undefined,
                      outlineOffset: s.is_go_to ? "-2px" : undefined,
                    }}
                  >
                    <td className="text-center" style={{ verticalAlign: "middle" }}>
                      <button
                        type="button"
                        onClick={() => flipGoTo(s)}
                        disabled={viewerIsViewOnly || goToBusy === s.scenario_id}
                        title={s.is_go_to
                          ? "This is the Go-To (Adopted plan). Star another scenario to change it."
                          : "Set as Adopted plan (Go To)"}
                        data-testid={`goto-star-${s.scenario_id}`}
                        style={{
                          position: "relative", left: 2,
                          background: "transparent",
                          border: "none",
                          padding: 4,
                          cursor: viewerIsViewOnly ? "not-allowed" : "pointer",
                          opacity: viewerIsViewOnly ? 0.4 : 1,
                        }}
                      >
                        <Star
                          size={18}
                          fill={s.is_go_to ? "#EA6A47" : "transparent"}
                          stroke={s.is_go_to ? "#EA6A47" : "#B4BEC7"}
                          strokeWidth={2}
                        />
                      </button>
                    </td>
                    <td className="text-center" style={{ verticalAlign: "middle" }}>
                      <input
                        type="checkbox"
                        checked={s.is_go_to || !!s.include_in_report}
                        disabled={s.is_go_to || viewerIsViewOnly || includeBusy === s.scenario_id}
                        onChange={() => flipInclude(s)}
                        title={s.is_go_to
                          ? "The Go-To scenario is always included in the report"
                          : (!s.include_in_report && includedCount >= MAX_INCLUDED
                            ? `Limit reached: up to ${MAX_INCLUDED} scenarios (including the Go-To)`
                            : "Include in the report")}
                        data-testid={`scenario-include-${s.scenario_id}`}
                        style={{ width: 15, height: 15, accentColor: "#1F3A5F", cursor: s.is_go_to || viewerIsViewOnly ? "not-allowed" : "pointer" }}
                      />
                    </td>
                    <td>
                      <div className="min-w-0">
                        <ScenarioIdLabel scenarioId={s.scenario_id} identifier={d.identifier} />
                        <div className="text-[10px] text-slate">{s.created_at && fmtDateTime(s.created_at)}</div>
                      </div>
                    </td>
                    <td>
                      <EditableScenarioField
                        scenario={s}
                        field="name"
                        pristineValue={s.name}
                        stagedValue={pendingEdits[s.scenario_id]?.name}
                        placeholder="[Unnamed Scenario]"
                        testidPrefix="scenario-name"
                        readOnly={!renamable}
                        readOnlyReason={renameLocked}
                        onStage={(field, v) => stageEdit(s.scenario_id, field, v)}
                      />
                    </td>
                    <td>
                      <span className={`block truncate ${creatorKey(s) === viewerId ? "font-bold text-navy" : "text-ink"}`} title={s.user_name || ""} data-testid={`scenario-user-readonly-${s.scenario_id}`}>
                        {creatorKey(s) === viewerId ? "You" : s.user_name || "—"}
                      </span>
                    </td>
                    <td className="num" style={{ textAlign: "center" }}>
                      <EditableScenarioNumber
                        scenario={s}
                        field="target_pct"
                        pristineValue={s.target_pct}
                        stagedValue={pendingEdits[s.scenario_id]?.target_pct}
                        kind="pct"
                        min={0} max={100} step={1}
                        readOnly
                        readOnlyReason={VALUES_LOCKED}
                        testidPrefix="scen-target"
                        onStage={(field, v) => stageEdit(s.scenario_id, field, v)}
                      />
                    </td>
                    <td className="num" style={{ textAlign: "center" }}>
                      <EditableScenarioNumber
                        scenario={s}
                        field="inflation_rate"
                        pristineValue={s.inflation_rate}
                        stagedValue={pendingEdits[s.scenario_id]?.inflation_rate}
                        kind="pctFractional"
                        min={-20} max={50} step={0.25}
                        readOnly
                        readOnlyReason={VALUES_LOCKED}
                        testidPrefix="scen-inflation"
                        onStage={(field, v) => stageEdit(s.scenario_id, field, v)}
                      />
                    </td>
                    <td className="num" style={{ textAlign: "center" }}>
                      <EditableScenarioNumber
                        scenario={s}
                        field="interest_rate"
                        pristineValue={s.interest_rate}
                        stagedValue={pendingEdits[s.scenario_id]?.interest_rate}
                        kind="pctFractional"
                        min={-20} max={50} step={0.25}
                        readOnly
                        readOnlyReason={VALUES_LOCKED}
                        testidPrefix="scen-interest"
                        onStage={(field, v) => stageEdit(s.scenario_id, field, v)}
                      />
                    </td>
                    <td className="num" style={{ textAlign: "center" }}>
                      <EditableScenarioNumber
                        scenario={s}
                        field="adjustment_pct"
                        pristineValue={s.adjustment_pct}
                        stagedValue={pendingEdits[s.scenario_id]?.adjustment_pct}
                        kind="pct"
                        min={-100} max={100} step={1}
                        readOnly
                        readOnlyReason={VALUES_LOCKED}
                        testidPrefix="scen-adjustment"
                        onStage={(field, v) => stageEdit(s.scenario_id, field, v)}
                      />
                    </td>
                    <td className="num" style={{ textAlign: "center" }}>
                      <CenteredNum ch={w.adj}>
                      <EditableScenarioNumber
                        scenario={s}
                        field="adjustment_period"
                        pristineValue={s.adjustment_period}
                        stagedValue={pendingEdits[s.scenario_id]?.adjustment_period}
                        kind="int"
                        min={0} max={30} step={1}
                        readOnly
                        readOnlyReason={VALUES_LOCKED}
                        testidPrefix="scen-adj-period"
                        onStage={(field, v) => stageEdit(s.scenario_id, field, v)}
                      />
                      </CenteredNum>
                    </td>
                    <td className="num" style={{ textAlign: "center" }}><CenteredNum ch={w.y1}>{fmtCurrency(d.year1_budget)}</CenteredNum></td>
                    <td
                      className="num"
                      style={{
                        textAlign: "center",
                        color: d.special_assessments > 0.5 ? "#EA6A47" : undefined,
                        fontWeight: d.special_assessments > 0.5 ? 600 : 400,
                      }}
                    >
                      <CenteredNum ch={w.sa}>{fmtCurrency(d.special_assessments)}</CenteredNum>
                    </td>
                    <td className="num" style={{ textAlign: "center" }} data-testid={`scenario-final-budget-${s.scenario_id}`}><CenteredNum ch={w.fy}>{fmtCurrency(d.final_year_budget)}</CenteredNum></td>
                    <td className="num" style={{ textAlign: "center" }} data-testid={`scenario-final-funding-${s.scenario_id}`}>{fmtPct(d.final_funding_pct, 0)}</td>
                    {/* 2026-03-09 (Dave) — element_changes_count cell
                        removed with the severed-scenarios rollout.
                        Element history lives in a dedicated drawer
                        on the Elements tab. */}
                    <td className="text-center">
                      {!mutable ? (
                        <span
                          className="text-slate text-[10px]"
                          title={
                            lockedReason ||
                            (s.is_baseline
                              ? "Element Baseline cannot be deleted right now"
                              : "You cannot delete this scenario")
                          }
                          data-testid={`delete-locked-${s.scenario_id}`}
                        >
                          —
                        </span>
                      ) : (
                        <button
                          onClick={() => requestDelete(s)}
                          disabled={busy === s.scenario_id}
                          className="text-coral hover:bg-coral-wash p-1"
                          data-testid={`delete-${s.scenario_id}`}
                          title={
                            s.is_baseline
                              ? "Delete this Element Baseline (the oldest saved scenario becomes the new baseline)"
                              : "Delete this scenario"
                          }
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {scenarios.length > 0 && visibleScenarios.length === 0 && (
                <tr>
                  <td colSpan={15} className="text-center text-slate py-6" data-testid="scenario-history-filter-empty">
                    No scenarios match the selected creators. Tick <span className="font-semibold text-navy">All</span> to see every scenario.
                  </td>
                </tr>
              )}
              {scenarios.length === 0 && (
                <tr>
                  <td colSpan={15} className="text-center text-slate py-6">
                    No scenarios saved yet. Change any variable then press{" "}
                    <span className="font-semibold text-navy">Save</span> to store one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {namePrompt && (
          <GoToNameDialog
            identifier={namePrompt.row.identifier}
            busy={namePrompt.busy}
            error={namePrompt.error}
            onSubmit={submitGoToName}
            onCancel={() => setNamePrompt(null)}
          />
        )}


        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-rule bg-navy-wash">
          {goToError ? (
            <div className="text-[11px] font-semibold" style={{ color: "#C2410C" }} data-testid="goto-error">
              {goToError}
            </div>
          ) : (
            <div className="text-[11px] text-slate whitespace-nowrap" data-testid="scenario-history-tips">
              <span className="text-navy font-semibold">Tips:</span> Click any column header to sort · starring a scenario makes it the Go-To that loads for everyone (unsaved edits are lost) · Select up to 3 Scenarios for inclusion in your Reserve Budget Report.
            </div>
          )}
          {/* 2026-02-27 (evening, Dave) — Save / Discard for staged
              inline edits (see `pendingEdits` at the top of this
              component). Buttons appear only when there is
              something to save so the footer stays quiet
              day-to-day. Error surfaces inline in coral. */}
          <div className="flex items-center gap-2">
            {saveError && (
              <div
                className="text-[11px] text-coral font-semibold mr-1"
                data-testid="scenario-history-save-error"
              >
                {saveError}
              </div>
            )}
            {pendingCount > 0 && (
              <>
                <span
                  className="text-[11px] text-slate mr-1"
                  data-testid="scenario-history-pending-count"
                >
                  {pendingCount} unsaved {pendingCount === 1 ? "row" : "rows"}
                </span>
                <button
                  onClick={discardEdits}
                  disabled={savingEdits}
                  className="border border-rule text-navy hover:bg-white px-3 py-1 text-[12px] font-semibold disabled:opacity-50"
                  data-testid="scenario-history-discard"
                >
                  Discard
                </button>
                <button
                  onClick={flushEdits}
                  disabled={savingEdits}
                  className="bg-coral text-white hover:bg-[#D25A38] px-3 py-1 text-[12px] font-semibold disabled:opacity-50"
                  data-testid="scenario-history-save"
                >
                  {savingEdits ? "Saving…" : "Save"}
                </button>
              </>
            )}
            <button
              onClick={saveAndClose}
              disabled={savingEdits}
              className="bg-navy text-white hover:bg-[#12406B] px-3 py-1 text-[12px] font-semibold disabled:opacity-50"
              data-testid="scenario-history-done"
            >
              {pendingCount > 0 ? "Save & Close" : "Done"}
            </button>
          </div>
        </div>
      </div>

      {/* Slice E2 — Element change delta popup */}
      {deltaScenario && (
        <ElementChangesModal
          scenario={deltaScenario}
          onClose={() => setDeltaScenario(null)}
        />
      )}

      {/* Branded delete-scenario confirmation (replaces window.confirm) */}
      {pendingDelete && (
        <ConfirmActionModal
          title={pendingDelete.is_baseline ? "Delete Element Baseline" : "Delete Scenario"}
          message={
            pendingDelete.is_baseline ? (
              <>
                This will permanently remove the Element Baseline. The oldest saved
                scenario{" "}
                {pendingReplacement && (
                  <>
                    —{" "}
                    <span className="font-semibold text-navy">
                      {pendingReplacement.name}
                    </span>{" "}
                    —{" "}
                  </>
                )}
                will become the new Element Baseline and its element changes will be
                baked into the plan year.{" "}
                <span className="font-semibold text-coral">
                  This cannot be undone.
                </span>
              </>
            ) : (
              <>
                {/* 2026-09-27 (Dave) — copy refreshed post-severing.
                    Non-baseline scenarios no longer carry element
                    overrides — the 5 sliders (+ beginning balance)
                    are the only thing lost on delete. */}
                This will permanently remove the scenario and its
                slider variables.{" "}
                <span className="font-semibold text-coral">This cannot be undone.</span>
              </>
            )
          }
          detail={pendingDelete.name}
          confirmLabel={pendingDelete.is_baseline ? "Delete Element Baseline" : "Delete Scenario"}
          cancelLabel="Cancel"
          variant="danger"
          busy={busy === pendingDelete.scenario_id}
          onCancel={() => { if (!busy) setPendingDelete(null); }}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
}
